import { WC_CHANNELS, WC_LIMITS, type WcControl, type WcWorkerRecoveryMessage } from '../protocol/WebCodecsTypes';
import { WebCodecsWire } from '../protocol/WebCodecsWire';
import { WcStats, type WebCodecsClientOptions, type WcClientStats, type WcWorkerOutput, type WcWorkerPort } from './WebCodecsClientTypes';
import { WebCodecsIngress } from './WebCodecsIngress';

/** Owns only the native video peer. Canvas ownership is one-time; restart requires a fresh surface. */
export class WebCodecsClient {
  private static readonly transferred = new WeakSet<HTMLCanvasElement>();
  private canvas: HTMLCanvasElement | null = null;
  private peer: RTCPeerConnection | null = null;
  private worker: WcWorkerPort | null = null;
  private ingress: WebCodecsIngress | null = null;
  private unlisten: (() => void) | null = null;
  private channels: RTCDataChannel[] = [];
  private candidates: RTCIceCandidateInit[] = [];
  private chain = Promise.resolve();
  private transitions = 0;
  private generation = 0;
  private session = '';
  private epoch = 0;
  private visible = true;
  private failed = false;
  private stats = WcStats.empty();
  private lastRecoveryUs = -Infinity;
  private presentedFrames = 0;
  private maxPresentGapMs = 0;
  private lastPresentedAtMs = 0;
  private needsKeyframe = false;
  private readonly latencySamplesUs: number[] = [];
  constructor(private readonly options: WebCodecsClientOptions) {}

  attachCanvas(canvas: HTMLCanvasElement): void {
    if (this.peer || WebCodecsClient.transferred.has(canvas) || typeof canvas.transferControlToOffscreen !== 'function')
      throw new Error('A fresh transferable canvas is required');
    this.canvas = canvas;
  }
  async start(session: string): Promise<RTCSessionDescriptionInit> {
    if (this.peer || !this.canvas || this.canvas.isConnected === false) throw new Error('Attach a fresh canvas before starting');
    WebCodecsWire.EncodeControl({ v: 1, type: 'close', session, reason: '' });
    const generation = ++this.generation; this.failed = false; this.session = session; this.epoch = 0; this.stats = WcStats.empty(); this.lastRecoveryUs = -Infinity;
    this.presentedFrames = 0; this.maxPresentGapMs = 0; this.lastPresentedAtMs = 0; this.needsKeyframe = false; this.latencySamplesUs.length = 0;
    try {
      const peer = this.options.createPeer(); this.peer = peer;
      const worker = this.options.createWorker(); this.worker = worker;
      this.ingress = new WebCodecsIngress(worker, () => this.requestRecovery());
      this.unlisten = worker.onMessage(message => { if (generation === this.generation) this.fromWorker(message); });
      const canvas = this.canvas;
      WebCodecsClient.transferred.add(canvas); this.canvas = null;
      if (worker.startWithCanvas) worker.startWithCanvas(session, canvas);
      else {
        const offscreen = canvas.transferControlToOffscreen();
        worker.postMessage({ type: 'start', session, canvas: offscreen }, [offscreen]);
      }
      worker.postMessage({ type: 'visibility', visible: this.visible });
      peer.onicecandidate = event => { if (generation === this.generation && event.candidate) this.options.onCandidate(event.candidate.toJSON()); };
      peer.onconnectionstatechange = () => {
        if (generation === this.generation && ['failed', 'closed'].includes(peer.connectionState)) this.fail('Native peer connection failed');
      };
      peer.ondatachannel = event => { event.channel.close(); if (generation === this.generation) this.fail('Unexpected remotely-created channel'); };
      for (const policy of Object.values(WC_CHANNELS)) {
        const channel = peer.createDataChannel(policy.label, policy); channel.binaryType = 'arraybuffer';
        channel.onmessage = event => { if (generation === this.generation) this.receive(channel, event.data); };
        channel.onclose = () => { if (generation === this.generation) this.fail('Native video channel closed'); };
        channel.onerror = () => { if (generation === this.generation) this.fail('Native video channel error'); };
        this.channels.push(channel);
      }
      const offer = await peer.createOffer(); this.current(generation);
      await peer.setLocalDescription(offer); this.current(generation);
      return { type: 'offer', sdp: peer.localDescription?.sdp ?? offer.sdp };
    } catch (error) { if (generation === this.generation) this.fail('Native video startup failed'); throw error; }
  }
  applyAnswer(sdp: string): Promise<void> {
    return this.serialize(async (peer, generation) => {
      if (sdp.length > 60000 || (sdp.match(/^m=/gm) ?? []).length !== 1 || !/^m=application /m.test(sdp)) throw new Error('Expected data-only SDP');
      await peer.setRemoteDescription({ type: 'answer', sdp }); this.current(generation);
      for (const candidate of this.candidates.splice(0)) { await peer.addIceCandidate(candidate); this.current(generation); }
    });
  }
  addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if ((candidate.candidate?.length ?? 0) > 4096 || (candidate.sdpMid?.length ?? 0) > 256) return Promise.reject(new Error('Oversized ICE candidate'));
    return this.serialize(async (peer, generation) => {
      if (peer.remoteDescription) { await peer.addIceCandidate(candidate); this.current(generation); }
      else { if (this.candidates.length >= 256) throw new Error('Early ICE queue exceeded'); this.candidates.push(candidate); }
    });
  }
  setVisible(visible: boolean): void { this.visible = visible; this.worker?.postMessage({ type: 'visibility', visible }); }
  /** Resets the decoder and asks for a keyframe without leaving the WebCodecs peer. */
  restart(): void {
    if (this.failed || !this.worker) return;
    this.needsKeyframe = true;
    this.worker.postMessage({ type: 'restart' });
  }
  getStats(): WcClientStats {
    return { ...this.stats, framesPresented: Math.max(this.stats.framesPresented, this.presentedFrames),
      maxPresentGapMs: this.maxPresentGapMs, lastPresentedAtMs: this.lastPresentedAtMs, needsKeyframe: this.needsKeyframe,
      drainLatencySamplesUs: () => this.latencySamplesUs.splice(0, 2048) };
  }
  close(): void {
    ++this.generation; this.unlisten?.(); this.unlisten = null; this.ingress?.close(); this.ingress = null;
    const worker = this.worker; this.worker = null;
    if (worker) { try { worker.postMessage({ type: 'close' }); } catch { /* Worker may already have failed. */ } finally { worker.terminate(); } }
    for (const channel of this.channels) { channel.onmessage = channel.onclose = channel.onerror = null; channel.close(); }
    this.channels = []; const peer = this.peer; this.peer = null;
    if (peer) { peer.onicecandidate = peer.onconnectionstatechange = peer.ondatachannel = null; peer.close(); }
    this.candidates = []; this.chain = Promise.resolve(); this.transitions = 0; this.session = ''; this.epoch = 0;
  }
  private receive(channel: RTCDataChannel, data: unknown): void {
    try {
      if (channel.label === WC_CHANNELS.video.label) {
        if (!(data instanceof ArrayBuffer) || data.byteLength > WC_LIMITS.messageBytes) throw new Error('Invalid media message');
        this.ingress?.fragment(data, this.options.nowUs()); return;
      }
      // Pings and source heartbeats arrive on the unordered, unreliable feedback channel (each is superseded by
      // the next); a lost one on the ordered control channel would hold a Configure behind its retransmission.
      // A Configure arrives on both: the reliable copy always lands, the unreliable copy may land first on a
      // path that is dropping chunks. Epochs only move forward, so whichever copy is second is ignored.
      const unreliable = channel.label === WC_CHANNELS.feedback.label;
      if ((channel.label !== WC_CHANNELS.control.label && !unreliable) || typeof data !== 'string' || data.length > WC_LIMITS.controlBytes) throw new Error('Invalid message direction');
      const message = WebCodecsWire.DecodeControl(new TextEncoder().encode(data));
      if (!message) throw new Error('Invalid control message');
      if (message.session !== this.session) return;
      if (!(unreliable ? ['configure', 'source', 'ping'] : ['configure', 'source', 'ping', 'close']).includes(message.type)) throw new Error('Invalid control direction');
      if (message.type === 'close') { this.fail('Host closed native video'); return; }
      if (message.type === 'configure') {
        if (message.config.epoch <= this.epoch) return;
        this.epoch = message.config.epoch;
      }
      if (message.type === 'source' && message.epoch !== this.epoch) return;
      if (!this.ingress?.control(message)) this.fail('Control ingress budget exceeded');
    } catch { this.fail('Invalid native video transport message'); }
  }
  private fromWorker(output: WcWorkerOutput): void {
    try {
      if (output.type === 'ingressReleased') { this.ingress?.release(output.bytes); return; }
      if (output.type === 'control') {
        if (output.message.session === this.session && (!('epoch' in output.message) || output.message.epoch === this.epoch))
          this.sendControl(output.message);
      } else if (output.type === 'feedback') {
        if (output.message.session === this.session && output.message.epoch === this.epoch) {
          const bytes = WebCodecsWire.EncodeFeedback(output.message), channel = this.channels[2];
          if (channel?.readyState === 'open' && channel.bufferedAmount + bytes.length <= 16384) channel.send(new TextDecoder().decode(bytes));
        }
      } else if (output.type === 'stats') {
        if (output.stats.epoch === this.epoch) this.stats = { ...WcStats.empty(), ...output.stats, drainLatencySamplesUs: () => [] };
      } else if (output.type === 'stalled' || output.type === 'needsKeyframe' || output.type === 'presented' || output.type === 'unsupported') {
        this.recovery(output);
      } else if (output.type === 'event') {
        const event = output.event;
        if (event.type === 'failed') this.fail(event.reason);
        else if (event.type === 'unsupported') this.options.onEvent(event);
        else if ((event.type === 'configured' ? event.config.epoch : event.epoch) === this.epoch) {
          if (event.type === 'presented') { this.needsKeyframe = false; this.lastPresentedAtMs = this.options.nowUs() / 1000; }
          this.options.onEvent(event);
        }
      }
    } catch { this.fail('Worker output or channel send failed'); }
  }
  private sendControl(message: WcControl): void {
    const channel = this.channels[0], bytes = WebCodecsWire.EncodeControl(message);
    if (!channel || channel.readyState !== 'open' || channel.bufferedAmount + bytes.length > 65536) throw new Error('Control channel unavailable');
    channel.send(new TextDecoder().decode(bytes));
  }
  private recovery(message: WcWorkerRecoveryMessage): void {
    if (message.type === 'unsupported') { this.options.onEvent(message); return; }
    if (message.epoch !== this.epoch) return;
    if (message.type === 'stalled') this.options.onEvent(message);
    else if (message.type === 'needsKeyframe') { this.needsKeyframe = true; this.options.onEvent(message); }
    else {
      this.presentedFrames = message.presentedFrames;
      this.maxPresentGapMs = Math.max(this.maxPresentGapMs, message.maxGapMs);
      this.lastPresentedAtMs = this.options.nowUs() / 1000;
      this.needsKeyframe = false;
      for (const sample of message.latencyUs) {
        if (!Number.isFinite(sample) || sample < 0) continue;
        this.latencySamplesUs.push(Math.round(sample));
        if (this.latencySamplesUs.length > 2048) this.latencySamplesUs.splice(0, this.latencySamplesUs.length - 2048);
      }
    }
  }
  private requestRecovery(): void {
    const now = this.options.nowUs();
    if (!this.epoch || now - this.lastRecoveryUs < 200000) return;
    this.lastRecoveryUs = now;
    this.sendControl({ v: 1, type: 'keyframe', session: this.session, epoch: this.epoch, afterFrameId: 0 });
  }
  private serialize(action: (peer: RTCPeerConnection, generation: number) => Promise<void>): Promise<void> {
    const peer = this.peer, generation = this.generation;
    if (!peer || ++this.transitions > 256) { this.fail('Signaling queue unavailable'); return Promise.reject(new Error('Signaling queue unavailable')); }
    const result = this.chain.then(async () => { this.current(generation); await action(peer, generation); });
    this.chain = result.catch(() => { if (generation === this.generation) this.fail('Native signaling failed'); })
      .finally(() => { if (generation === this.generation) --this.transitions; });
    return result;
  }
  private current(generation: number): void { if (generation !== this.generation || !this.peer) throw new Error('Session retired'); }
  private fail(reason: string): void {
    if (this.failed) return;
    this.failed = true; this.close(); this.options.onEvent({ type: 'failed', reason: reason.slice(0, 256) });
  }
}
